// Location: src/components/admin/ScheduleCard.tsx
// Purpose: Main schedule card component - orchestrates smaller components
// Scope: Container component that brings together all schedule display elements
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { ScheduleStatusBadge } from './ScheduleStatusBadge';
import { Id } from '@/convex/_generated/dataModel';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';

// Import modular components
import { ScheduleHeader } from './schedule/ScheduleHeader';
import { SchedulePodInfo } from './schedule/SchedulePodInfo';
import { ScheduleCommentsPerRun } from './schedule/ScheduleCommentsPerRun';
import { ScheduleFrequencyInfo } from './schedule/ScheduleFrequencyInfo';
import { ScheduleTimingInfo } from './schedule/ScheduleTimingInfo';
import { ScheduleStatusInfo } from './schedule/ScheduleStatusInfo';
import { ScheduleRecentComments } from './schedule/ScheduleRecentComments';
import {
	getPodModeCapability,
	getPodPlatformLabel,
} from '@/types/pods';

// Timezone helper moved to ScheduleFrequencyInfo component

interface _CommentUrl {
	url: string;
	username: string;
	createdAt: string;
}

interface ScheduleCardProps {
	schedule: any;
	onDelete: (id: Id<'schedules'>, type: string) => void;
	onRunNow: (scheduleId: Id<'schedules'>) => void;
	formatScheduleType: (type: string) => string;

	getActionDisplay: (schedule: any) => string | null;

	allPods: any[];
	isSuperAdmin: boolean;
	isAdmin: boolean;
	user?: any; // Add user prop for ownership validation
}

// Brand voice constants moved to SchedulePodInfo component

// This is a type assertion function to help TypeScript understand ID types
// function asId<T extends string>(id: any): Id<T> {
// 	return id as Id<T>;
// }

export const ScheduleCard = React.memo(function ScheduleCard({
	schedule,
	onDelete,
	onRunNow,
	formatScheduleType,
	getActionDisplay,
	allPods,
	isSuperAdmin,
	isAdmin,
	user,
}: ScheduleCardProps) {
	console.log('Schedule metadata:', {
		id: schedule._id,
		type: schedule.type,
		metadata: schedule.metadata,
		brandVoice: schedule.metadata?.brandVoice,
		podBrandVoice: schedule.pod?.brandVoice,
	});

	const pod = allPods?.find((p) => p._id === schedule.targetId);
	const podCapability = pod
		? getPodModeCapability(pod.platform, pod.mode || 'swarm')
		: null;
	const runNowDisabledReason =
		pod && podCapability && !podCapability.enabled
			? podCapability.reason ||
				`${getPodPlatformLabel(pod.platform)} ${pod.mode || 'swarm'} schedules are disabled for this spec`
			: undefined;

	// 🔒 SECURITY: Check if current user owns this schedule's pod
	const userOwnsSchedule = user && pod && pod.userId === user.id;

	// Debug ownership validation
	console.log('🔒 Ownership Check:', {
		scheduleId: schedule._id,
		scheduleType: schedule.type,
		podId: schedule.targetId,
		podUserId: pod?.userId,
		currentUserId: user?.id,
		userOwnsSchedule,
		isSuperAdmin,
		showRunButton: isSuperAdmin || userOwnsSchedule,
	});

	const brandVoice = pod?.brandVoice?.id || 'prometheus';

	// Get real-time URL count for scraper schedules
	const podUrls = useQuery(
		api.urls.list,
		schedule.type === 'post_scraper' && pod
			? {
					podId: schedule.targetId,
					profileKey:
						pod.mode === 'broadcast'
							? pod.members?.[0]?.uploadPostProfileKey
							: null,
				}
			: 'skip'
	);

	// Count active URLs
	const activeUrlCount = React.useMemo(() => {
		if (!podUrls) return 0;
		return podUrls.filter((url: any) => url.isActive && !url.isArchived)
			.length;
	}, [podUrls]);

	// Enhanced getActionDisplay that includes real-time URL count
	const getEnhancedActionDisplay = React.useCallback(() => {
		if (schedule.type === 'post_scraper') {
			const actionCount = schedule.metadata?.actionCount;
			const postsPerUsername = actionCount ?? 50;
			const urlCount = activeUrlCount || schedule.metadata?.urlCount || 0;
			const totalPosts = postsPerUsername * urlCount;

			if (urlCount === 0) {
				return 'No active URLs to import';
			} else if (urlCount === 1) {
				return `Importing 1 URL ${totalPosts} latest posts`;
			} else {
				return `Importing ${urlCount} URLs (50 ea.) Total ${totalPosts} latest posts`;
			}
		}

		// For non-scraper schedules, use the original function
		return getActionDisplay(schedule);
	}, [schedule, activeUrlCount, getActionDisplay]);

	const isTaskRunning = React.useCallback((schedule: any) => {
		// 🚨 EMERGENCY FIX: More aggressive detection of running tasks
		const isRunning = schedule.isRunning;
		const hasPublishingStatus =
			schedule.lastStatus?.status === 'PUBLISHING';
		const hasGeneratingStatus =
			schedule.lastStatus?.status === 'GENERATING';
		const recentlyUpdated =
			schedule.lastStatus?.timestamp &&
			new Date().getTime() -
				new Date(schedule.lastStatus.timestamp).getTime() <
				300000; // 5 minutes

		return (
			isRunning ||
			(hasPublishingStatus && recentlyUpdated) ||
			(hasGeneratingStatus && recentlyUpdated)
		);
	}, []);

	const [showUrls, setShowUrls] = React.useState(false);

	const commentUrls = useQuery(
		api.twitterXComments.getCommentUrlsBySchedule,
		{ scheduleId: schedule._id, limit: 5 }
	);

	// Add mutation for updating schedule metadata
	const updateScheduleMetadata = useMutation(api.schedules.updateMetadata);
	const resetSchedule = useMutation(api.schedules.resetSchedule);

	// Get ready comments count for accurate scheduling display
	const readyCommentsCount =
		useQuery(api.podCounters.getReadyCommentsCount, {
			podId: schedule.targetId,
		}) ?? 0; // Fallback to 0 if query fails

	// 🎯 ACCURATE COUNT: Use efficient counter system for display
	const accurateReadyCommentsCount =
		useQuery(api.podCounters.getReadyCommentsCount, {
			podId: schedule.targetId,
		}) ?? 0;

	// 🔧 FIX: Only show actual ready comments, never include pending comments
	// The UI should reflect what will actually be published (ready comments only)
	const displayReadyCount =
		accurateReadyCommentsCount > 0
			? accurateReadyCommentsCount
			: readyCommentsCount > 0
				? readyCommentsCount
				: 0; // If no ready comments found, show 0 (don't include pending)

	// 🎯 UNIFIED DATA SOURCE: Use the same query for all pending posts displays
	// This ensures consistency between scraper and publishing displays
	const pendingPostsCount =
		useQuery(api.twitter.getPendingPostsCount, {
			podId: schedule.targetId,
		}) ?? 0;

	// Keep the old counter query as fallback for debugging
	const counterPendingPostsCount =
		useQuery(api.podCounters.getCounterValue, {
			podId: schedule.targetId,
			counterType: 'pending_posts',
		}) ?? 0;

	// Debug: Log the actual values we're getting
	console.log('🔍 Counter Debug:', {
		scheduleId: schedule._id,
		podId: schedule.targetId,
		readyCommentsCount,
		accurateReadyCommentsCount,
		displayReadyCount,
		pendingPostsCount,
		counterPendingPostsCount,
		lastRunMessage: schedule.lastRunStatus?.message,
		dataSourceComparison: {
			liveQuery: pendingPostsCount,
			counterSystem: counterPendingPostsCount,
			difference: Math.abs(pendingPostsCount - counterPendingPostsCount),
		},
		note: 'FIXED: UI calculations now match publishing task logic exactly (ready comments only)',
	});

	// Debug what we're receiving
	React.useEffect(() => {
		if (commentUrls) {
			console.log('🎯 ScheduleCard received commentUrls:', {
				scheduleId: schedule._id,
				scheduleType: schedule.type,
				receivedCount: commentUrls.length,
				urls: commentUrls.map((c: any) => ({
					url: c.url,
					username: c.username,
				})),
			});
		}
	}, [commentUrls, schedule._id, schedule.type]);

	// Handler for comments per run change
	const handleCommentsPerRunChange = async (value: string) => {
		try {
			await updateScheduleMetadata({
				scheduleId: schedule._id,
				metadata: {
					mode: schedule.metadata?.mode,
					platform: schedule.metadata?.platform,
					profileCount: schedule.metadata?.profileCount,
					urlCount: schedule.metadata?.urlCount,
					isTestMode: schedule.metadata?.isTestMode,
					actionCount: schedule.metadata?.actionCount,
					conversationId: schedule.metadata?.conversationId,
					commentsPerRun: value as 'ALL' | 'ONE' | 'DISTRIBUTED',
				},
			});
		} catch (error) {
			console.error('Failed to update comments per run:', error);
		}
	};

	// Handler for reset schedule
	const handleResetSchedule = async () => {
		try {
			await resetSchedule({
				scheduleId: schedule._id,
			});
		} catch (error) {
			console.error('Failed to reset schedule:', error);
		}
	};

	// Get current comments per run value
	const currentCommentsPerRun =
		schedule.metadata?.commentsPerRun || 'DISTRIBUTED';

	// Get current comments per run value with permission check
	const effectiveCommentsPerRun =
		currentCommentsPerRun === 'ALL' && !isSuperAdmin
			? 'DISTRIBUTED'
			: currentCommentsPerRun;

	// Calculate distributed amount using same logic as commentPublishing.ts
	const calculateDistributedAmount = React.useCallback(
		(totalComments: number) => {
			// Match publishing task logic exactly - no early return for 0
			// Publishing task: Math.ceil(readyCommentsCount / 4) then Math.max(1, result)
			const basePerRun = Math.ceil(totalComments / 4);
			const distributed = Math.max(1, basePerRun);

			// But if there are 0 comments, show 0 since that's what will actually be published
			// (Publishing task tries 1 but finds 0 and publishes 0)
			return totalComments === 0 ? 0 : distributed;
		},
		[]
	);

	// Get the calculated amount for display
	const getDisplayAmount = React.useCallback(() => {
		switch (effectiveCommentsPerRun) {
			case 'ALL': {
				// For ALL mode, only ready comments will be published (publishing task doesn't generate comments)
				// Show what will actually be published, not what's theoretically available
				return displayReadyCount; // Only ready comments can be published
			}
			case 'ONE':
				return Math.min(1, displayReadyCount); // Can't publish more than available
			case 'DISTRIBUTED': {
				// For DISTRIBUTED mode, only use ready comments (matches publishing task logic exactly)
				// Publishing task divides ready comments by 4, not ready + pending
				return calculateDistributedAmount(displayReadyCount);
			}
			default:
				return typeof effectiveCommentsPerRun === 'number'
					? Math.min(effectiveCommentsPerRun, displayReadyCount)
					: Math.min(
							Number(effectiveCommentsPerRun) || 1,
							displayReadyCount
						);
		}
	}, [
		effectiveCommentsPerRun,
		displayReadyCount,
		calculateDistributedAmount,
	]);

	// Debug counter values
	React.useEffect(() => {
		console.log('📊 ScheduleCard Counter Debug:', {
			scheduleId: schedule._id,
			scheduleType: schedule.type,
			podId: schedule.targetId,
			readyCommentsCount,
			accurateReadyCommentsCount,
			displayReadyCount,
			pendingPostsCount,
			counterPendingPostsCount,
			effectiveCommentsPerRun,
			calculatedAmount: getDisplayAmount(),
			isCommentPublishing:
				schedule.type === 'comments' ||
				schedule.type === 'profile_comment',
			distributionCalculation:
				schedule.type === 'comments' ||
				schedule.type === 'profile_comment'
					? `${displayReadyCount} ready (only) ÷ 4 = ${Math.ceil(displayReadyCount / 4)} (pending posts not included in calculation)`
					: 'N/A (not comment publishing)',
			fixNote:
				'FIXED: Both displayReadyCount and getDisplayAmount now use only ready comments (matches publishing task logic)',
		});
	}, [
		schedule._id,
		schedule.type,
		schedule.targetId,
		readyCommentsCount,
		accurateReadyCommentsCount,
		displayReadyCount,
		pendingPostsCount,
		counterPendingPostsCount,
		effectiveCommentsPerRun,
		getDisplayAmount,
	]);

	console.log('Brand Voice Debug:', {
		podId: schedule.targetId,
		pod: pod,
		brandVoice: pod?.brandVoice,
		finalBrandVoice: brandVoice,
	});

	return (
		<div className='mb-12 p-8 rounded-lg border border-purple-500/20 bg-background/80 backdrop-blur-sm shadow-md'>
			<div className='flex flex-col gap-4'>
				{/* Schedule Header with badges and action buttons */}
				<ScheduleHeader
					schedule={schedule}
					isSuperAdmin={isSuperAdmin}
					userOwnsSchedule={userOwnsSchedule}
					isTaskRunning={isTaskRunning(schedule)}
					formatScheduleType={formatScheduleType}
					onDelete={onDelete}
					onRunNow={onRunNow}
					onResetSchedule={handleResetSchedule}
					runNowDisabledReason={runNowDisabledReason}
				/>
				{runNowDisabledReason && (
					<div className='rounded-md border border-orange-300 bg-orange-50 p-3 text-sm text-orange-900 dark:border-orange-900/60 dark:bg-orange-950/40 dark:text-orange-200'>
						{runNowDisabledReason}
					</div>
				)}

				{/* Pod and Brand Voice Information */}
				<SchedulePodInfo
					schedule={schedule}
					allPods={allPods}
					brandVoice={brandVoice}
				/>

				{/* Comments Per Run Configuration */}
				<ScheduleCommentsPerRun
					schedule={schedule}
					isSuperAdmin={isSuperAdmin}
					userOwnsSchedule={userOwnsSchedule}
					isTaskRunning={isTaskRunning(schedule)}
					effectiveCommentsPerRun={effectiveCommentsPerRun}
					displayReadyCount={displayReadyCount}
					pendingPostsCount={pendingPostsCount}
					onCommentsPerRunChange={handleCommentsPerRunChange}
					getDisplayAmount={getDisplayAmount}
				/>

				<div className='mt-4'>
					<ScheduleStatusBadge
						status={schedule.status}
						isRunning={isTaskRunning(schedule)}
						lastStatus={schedule.lastStatus}
						runHistory={schedule.runHistory}
					/>
				</div>

				{/* Frequency and Action Information */}
				<ScheduleFrequencyInfo
					schedule={schedule}
					getActionDisplay={getEnhancedActionDisplay}
					pendingPostsCount={pendingPostsCount}
					displayReadyCount={displayReadyCount}
				/>

				{/* Timing Information */}
				<ScheduleTimingInfo schedule={schedule} />

				{/* Status Information */}
				<ScheduleStatusInfo schedule={schedule} />
			</div>

			{/* Recent Comments List */}
			<ScheduleRecentComments
				schedule={schedule}
				commentUrls={commentUrls}
			/>
		</div>
	);
});
